import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  apaDate,
  apaInTextAuthors,
  apaNameInitialsFirst,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  mlaAuthors,
  mlaDate,
  mlaInTextAuthors,
  naturalName,
  type PartialDate,
  type PersonName,
  parsePartialDate,
  tidyParts,
  withPeriod,
} from "@/lib/tool-kit/citation-parts";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mlaUrl,
  plain,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";
import {
  apaAmpersandList,
  beforeSubtitle,
  chicagoShortTitle,
  longDate,
  type MediaLabels,
  type MediaValues,
  mlaRoleNames,
  parsePeople,
} from "@/lib/tool-kit/media-sources/common";

/*
 * Online courses and MOOCs (Coursera, edX, FutureLearn, Khan Academy, LinkedIn
 * Learning), or one lecture or module from one, in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA Style, "Online course or MOOC references" (§10.12): "Jackson, M. O.,
 *   Leyton-Brown, K., & Shoham, Y. (n.d.). Game theory [MOOC]. Coursera.
 *   https://www.coursera.org/learn/game-theory-1" and, for a lecture, "Tangen, J.
 *   (2016). Episode 2: I heard a tapping somewhat louder than before [MOOC lecture].
 *   In E. MacKenzie, J. Tangen, & M. Thompson, The science of everyday thinking. edX."
 *   (course italic, lecture roman, faculty in the editor position; the link may be the
 *   course's main page because lectures sit behind a login). APA Style, "PowerPoint
 *   slide or lecture note references": material in a learning management system is
 *   cited with the site's name and login URL ("Canvas@FNU. https://fnu.onelogin.com/
 *   login") only when your audience can open it; otherwise cite it as a personal
 *   communication (§8.9), in the text only.
 * - MLA 9: MLA Style Center, "How do I cite course materials?" — a course's name is
 *   "capitalized title-style, not in italics, and without quotation marks"; "How do I
 *   cite an online lesson?" — "Venard, Lourdes. Lesson on nominalizations, wordy
 *   language, and passive language. Copyediting: Intermediate, Editorial Freelancers
 *   Association, 5 June-17 July 2018, www.the-efa.org/…"; a website or learning
 *   management system that holds the course is a further container ("Blackboard",
 *   italic). MLA recommends an access date for an undated online work.
 * - Chicago 18: no MOOC example. The generator treats the course as website content,
 *   as the CMOS 18 Quick Guide does for web pages (§14.104): the title in quotation
 *   marks, the site, and "accessed" with the date when the page is undated. A lecture
 *   follows §14.115 (talks): its title in quotation marks with a description. Course
 *   materials readers cannot open are cited in notes only, like personal
 *   communications (§14.111).
 */

export const ONLINE_COURSE_FIELDS = {
  lecturers: "lecturers",
  lectureTitle: "lectureTitle",
  instructors: "instructors",
  title: "title",
  kind: "kind",
  platform: "platform",
  institution: "institution",
  date: "date",
  accessed: "accessed",
  url: "url",
  restricted: "restricted",
} as const;

const F = ONLINE_COURSE_FIELDS;

/** The whole course, or one lecture, video or module from it. */
export type CoursePart = "course" | "lecture";

export interface OnlineCourseLabels extends MediaLabels {
  apaNote: string;
  apaRestrictedNote: string;
  mlaNote: string;
  chicagoNote: string;
  chicagoRestrictedNote: string;
}

interface Course {
  part: CoursePart;
  isMooc: boolean;
  lecturers: PersonName[];
  lectureTitle: string;
  instructors: PersonName[];
  title: string;
  platform: string;
  institution: string;
  date: PartialDate | null;
  accessed: PartialDate | null;
  url: string;
  restricted: boolean;
}

function readCourse(part: CoursePart, values: MediaValues): Course {
  const isLecture = part === "lecture";
  return {
    part,
    isMooc: get(values, F.kind) !== "course",
    lecturers: isLecture ? parsePeople(get(values, F.lecturers)) : [],
    lectureTitle: isLecture
      ? capitalizeSubtitle(get(values, F.lectureTitle))
      : "",
    instructors: parsePeople(get(values, F.instructors)),
    title: capitalizeSubtitle(get(values, F.title)),
    platform: get(values, F.platform),
    institution: get(values, F.institution),
    date: parsePartialDate(get(values, F.date)),
    accessed: parsePartialDate(get(values, F.accessed)),
    url: get(values, F.url),
    restricted: get(values, F.restricted) === "true",
  };
}

/** Who wrote the cited part: the lecturer for a lecture, the instructors otherwise. */
function creators(course: Course): PersonName[] {
  if (course.part === "lecture" && course.lecturers.length > 0) {
    return course.lecturers;
  }
  return course.instructors;
}

function capitalizeFirst(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const RE_ENDS_IN_MARK = /[?!]$/;

function quotedTitle(title: string, mark: "," | "."): string {
  return RE_ENDS_IN_MARK.test(title) ? `“${title}”` : `“${title}${mark}”`;
}

/* ---------------------------------------------------------------------- APA */

function apaLabel(course: Course): string {
  const kind = course.isMooc ? "MOOC" : "Online course";
  return course.part === "lecture" ? `${kind} lecture` : kind;
}

function apaSource(course: Course): string {
  return [
    course.platform ? `${course.platform}${closer(course.platform)}` : "",
    course.url,
  ]
    .filter(Boolean)
    .join(" ");
}

function apaReference(course: Course): CitationParts {
  const people = creators(course);
  const date = `(${apaDate(course.date)}).`;
  const source = apaSource(course);
  const head: CitationParts = [];
  if (people.length > 0) {
    const authors = apaAuthors(people);
    head.push(plain(`${authors}${closer(authors)} ${date} `));
  }
  const dateAfterTitle = people.length > 0 ? "" : ` ${date}`;
  if (course.part === "course") {
    return tidyParts([
      ...head,
      italic(course.title),
      plain(` [${apaLabel(course)}].${dateAfterTitle} ${source}`),
    ]);
  }
  const faculty = apaAmpersandList(
    course.instructors.map(apaNameInitialsFirst)
  );
  return tidyParts([
    ...head,
    plain(`${course.lectureTitle} [${apaLabel(course)}].${dateAfterTitle} In `),
    plain(faculty ? `${faculty}, ` : ""),
    italic(course.title),
    plain(`. ${source}`),
  ]);
}

function apaPersonalCommunication(
  course: Course,
  narrative: boolean
): CitationParts {
  const who = apaAmpersandList(creators(course).map(apaNameInitialsFirst));
  const when = longDate(course.date) || "n.d.";
  if (narrative) {
    return [plain(`${who} (personal communication, ${when})`)];
  }
  return [plain(`(${who}, personal communication, ${when})`)];
}

function apaOutput(course: Course, labels: OnlineCourseLabels): CitationOutput {
  const people = creators(course);
  if (course.restricted && people.length > 0) {
    return {
      key: "apa",
      label: labels.apa,
      entries: [
        {
          label: labels.parenthetical,
          parts: apaPersonalCommunication(course, false),
        },
        {
          label: labels.narrative,
          parts: apaPersonalCommunication(course, true),
        },
      ],
      note: labels.apaRestrictedNote,
    };
  }
  const year = course.date ? String(course.date.year) : "n.d.";
  const title = course.part === "lecture" ? course.lectureTitle : course.title;
  const short = beforeSubtitle(titleCase(title));
  const parenthetical: CitationParts =
    people.length > 0
      ? [plain(`(${apaInTextAuthors(people)}, ${year})`)]
      : [plain("("), italic(short), plain(`, ${year})`)];
  const narrative: CitationParts =
    people.length > 0
      ? [plain(`${apaInTextAuthors(people, true)} (${year})`)]
      : [italic(short), plain(` (${year})`)];
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: apaReference(course) },
      { label: labels.parenthetical, parts: parenthetical },
      { label: labels.narrative, parts: narrative },
    ],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

/** The course's own elements: title (plain, title case), faculty, school, date. */
function mlaCourseElements(course: Course): string[] {
  const courseTitle = titleCase(course.title);
  if (course.part === "course") {
    return [course.institution, mlaDate(course.date)].filter(Boolean);
  }
  const faculty =
    course.instructors.length > 0
      ? `taught by ${mlaRoleNames(course.instructors.map(naturalName))}`
      : "";
  return [
    courseTitle,
    faculty,
    course.institution,
    mlaDate(course.date),
  ].filter(Boolean);
}

function mlaWorksCited(course: Course): CitationParts {
  const parts: CitationParts = [];
  const people = creators(course);
  if (people.length > 0) {
    const authors = mlaAuthors(people);
    parts.push(plain(`${authors}${closer(authors)} `));
  }
  if (course.part === "lecture") {
    parts.push(plain(`${quotedTitle(titleCase(course.lectureTitle), ".")} `));
  } else {
    const title = titleCase(course.title);
    parts.push(plain(`${title}${closer(title)} `));
  }
  const own = mlaCourseElements(course);
  const url = course.url ? mlaUrl(course.url) : "";
  if (own.length > 0) {
    const tail = course.platform ? "" : url;
    parts.push(plain(withPeriod([...own, tail].filter(Boolean).join(", "))));
  } else if (!course.platform && url) {
    parts.push(plain(`${url}.`));
  }
  if (course.platform) {
    parts.push(
      plain(" "),
      italic(course.platform),
      plain(url ? `, ${url}.` : ".")
    );
  }
  if (!course.date && course.accessed) {
    parts.push(plain(` Accessed ${mlaDate(course.accessed)}.`));
  }
  return tidyParts(parts);
}

function mlaOutput(course: Course, labels: OnlineCourseLabels): CitationOutput {
  const people = creators(course);
  const inText =
    people.length > 0
      ? `(${mlaInTextAuthors(people)})`
      : `(${beforeSubtitle(citedTitle(course))})`;
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: mlaWorksCited(course) },
      { label: labels.inText, parts: [plain(inText)] },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

function chicagoWhen(course: Course): string {
  if (course.date) {
    return longDate(course.date);
  }
  return course.accessed ? `accessed ${longDate(course.accessed)}` : "n.d.";
}

function chicagoNoteElements(course: Course): string[] {
  const description = course.part === "lecture" ? "lecture" : "online course";
  const courseTitle = course.part === "lecture" ? titleCase(course.title) : "";
  return [
    description,
    courseTitle,
    course.institution,
    course.platform,
    chicagoWhen(course),
    course.url,
  ].filter(Boolean);
}

function citedTitle(course: Course): string {
  return titleCase(
    course.part === "lecture" ? course.lectureTitle : course.title
  );
}

function chicagoNote(course: Course): CitationParts {
  const people = creators(course);
  const lead = people.length > 0 ? `${chicagoNoteAuthors(people)}, ` : "";
  const elements = chicagoNoteElements(course).join(", ");
  return [
    plain(
      `${lead}${quotedTitle(citedTitle(course), ",")} ${withPeriod(elements)}`
    ),
  ];
}

function chicagoShortNote(course: Course): CitationParts {
  const people = creators(course);
  const short = quotedTitle(chicagoShortTitle(citedTitle(course)), ".");
  return [
    plain(
      people.length > 0 ? `${chicagoShortAuthors(people)}, ${short}` : short
    ),
  ];
}

function chicagoBibliography(course: Course): CitationParts {
  const people = creators(course);
  const sentences: string[] = [];
  if (people.length > 0) {
    sentences.push(withPeriod(chicagoBibliographyAuthors(people)));
  }
  sentences.push(quotedTitle(citedTitle(course), "."));
  const description = course.part === "lecture" ? "Lecture" : "Online course";
  const courseTitle = course.part === "lecture" ? titleCase(course.title) : "";
  const dated = course.date || !course.accessed;
  const facts = [
    description,
    courseTitle,
    course.institution,
    course.platform,
    dated ? chicagoWhen(course) : "",
  ];
  sentences.push(withPeriod(facts.filter(Boolean).join(", ")));
  if (!dated) {
    sentences.push(withPeriod(capitalizeFirst(chicagoWhen(course))));
  }
  if (course.url) {
    sentences.push(`${course.url}.`);
  }
  return [plain(sentences.join(" "))];
}

function chicagoOutput(
  course: Course,
  labels: OnlineCourseLabels
): CitationOutput {
  const entries = [
    { label: labels.footnote, parts: chicagoNote(course) },
    { label: labels.shortNote, parts: chicagoShortNote(course) },
  ];
  if (!course.restricted) {
    entries.push({
      label: labels.bibliography,
      parts: chicagoBibliography(course),
    });
  }
  return {
    key: "chicago",
    label: labels.chicago,
    entries,
    note: course.restricted ? labels.chicagoRestrictedNote : labels.chicagoNote,
  };
}

export function wholeCourseIsReady(values: MediaValues): boolean {
  return get(values, F.title) !== "";
}

export function courseLectureIsReady(values: MediaValues): boolean {
  return get(values, F.lectureTitle) !== "" && get(values, F.title) !== "";
}

export function formatOnlineCourse(
  part: CoursePart,
  values: MediaValues,
  labels: OnlineCourseLabels
): CitationOutput[] {
  const course = readCourse(part, values);
  return [
    apaOutput(course, labels),
    mlaOutput(course, labels),
    chicagoOutput(course, labels),
  ];
}
